/*
 * Keycloak sign-in.
 *
 * Backstage ships no built-in API ref for a generic OIDC provider (there is a `githubAuthApiRef`
 * and friends, but nothing for "whatever OIDC server you happen to run"), so the frontend half is
 * three pieces: an ApiRef, an OAuth2 factory bound to the `oidc` provider id, and a sign-in page
 * that offers it.
 *
 * The provider id 'oidc' must match the key under auth.providers in app-config.yaml and the backend
 * module registered in packages/backend/src/index.ts. The IdP itself is dev-infra/auth/keycloak.yaml.
 *
 * Keycloak is the ONLY provider. The `guest` provider was removed on request, which means the
 * portal is unreachable whenever Keycloak is: the realm lives only on that pod's PVC and there is
 * no copy in git. Recovery is documented in docs/reference/keycloak.md — briefly, re-add 'guest'
 * to `providers` below, re-add the backend module in packages/backend/src/index.ts, and restore the
 * auth.providers.guest block in app-config.yaml.
 */
import {
  ApiBlueprint,
  configApiRef,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { OAuth2 } from '@backstage/core-app-api';
import type {
  BackstageIdentityApi,
  OAuthApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';

export const oidcAuthApiRef = createApiRef<
  OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'auth.oidc',
});

const oidcAuthApi = ApiBlueprint.make({
  name: 'oidc-auth',
  params: define =>
    define({
      api: oidcAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'oidc',
            title: 'Keycloak',
            icon: () => null,
          },
          // Must match auth.environment in app-config.yaml, or the backend serves a different
          // provider config than the frontend asks for.
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      const { SignInPage } = await import('@backstage/core-components');
      return props => (
        <SignInPage
          {...props}
          providers={[
            {
              id: 'oidc',
              title: 'Keycloak',
              message: 'Sign in with your True Relevance account',
              apiRef: oidcAuthApiRef,
            },
          ]}
        />
      );
    },
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [oidcAuthApi, signInPage],
});
